import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { ColorItem, ProjectLabel } from "./types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { BookmarkX, CheckIcon, PlusIcon, Tag } from "lucide-react";
import * as Checkbox from "@radix-ui/react-checkbox";
import { COLOR_LIST } from "@/utils/constants";
import { Input } from "../ui/input";
import clsx from "clsx";

interface Props {
  triggerElement: ReactNode;
  labels: Array<ProjectLabel>;
  onChange: (value: Array<string>) => void;
  addNew?: boolean;
  open?: boolean;
  title?: string;
  baseSelection?: Array<string>;
  clearString?: string;
  setOpen?: Dispatch<SetStateAction<boolean>>;
  onUpdateProjectLabels?: (newLabels: Omit<ProjectLabel, "id">) => void;
}

export function LabelDropdown({
  triggerElement,
  labels,
  open,
  addNew = true,
  baseSelection,
  title,
  clearString,
  setOpen,
  onUpdateProjectLabels,
  onChange,
}: Props) {
  const [newLabelVal, setNewLabelVal] = useState<string>("");
  const [page, setPage] = useState<string>("list");
  const [selectedLabels, setSelectedLabels] = useState<Array<string>>(
    baseSelection ?? []
  );

  const handleColorSelect = (color: ColorItem) => {
    const obj = {
      label: newLabelVal,
      color,
    };
    // @ts-expect-error TS2722
    onUpdateProjectLabels(obj);
    // @ts-expect-error TS2722
    setOpen(false);
    setPage("list");
  };

  const handleChange = useCallback(
    (id: string) => {
      const newArr = [...selectedLabels];
      const index = selectedLabels.indexOf(id);
      index == -1 ? newArr.push(id) : newArr.splice(index, 1);

      setSelectedLabels(newArr);
      onChange(newArr);
    },
    [selectedLabels]
  );

  useEffect(() => {
    if (!open) {
      setPage("list");
    } else {
      // @ts-expect-error TS2345
      setSelectedLabels(baseSelection);
    }
  }, [open]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        asChild
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {triggerElement}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={clsx(
          "w-[180px] ",
          page === "list" && "max-h-64 overflow-y-auto hide-scrollbar"
        )}
      >
        {page === "list" && (
          <>
            {title && (
              <>
                <div className="flex justify-between text-xs !p-1">{title}</div>
                <DropdownMenuSeparator />
              </>
            )}
            {clearString && labels?.length > 0 && (
              <>
                <DropdownMenuItem
                  className="text-xs !py-0.5 !pl-0.5 gap-1.5"
                  onClick={(e) => {
                    setSelectedLabels([]);
                    onChange([]);
                    e.stopPropagation();
                  }}
                  disabled={labels?.length === 0}
                >
                  <div className="border border-secondary-n-900 bg-transparent hover:bg-transparent h-6 w-6 text-xs rounded-md text-secondary-n-400 flex items-center justify-center">
                    <Tag className="size-4" />
                  </div>
                  {clearString}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {addNew && (
              <>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    setPage("create");
                    // @ts-expect-error TS2722
                    setOpen(true);
                  }}
                  className="flex justify-between text-xs cursor-pointer"
                >
                  New Label...
                  <PlusIcon className="w-4 h-4" />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {labels && labels.length > 0 && (
              <>
                {labels
                  .filter((label) => label?.color)
                  .map((item: ProjectLabel, index: number) => (
                    <DropdownMenuItem
                      key={index}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleChange(item.id);
                      }}
                      className="group text-xs cursor-pointer gap-1.5"
                    >
                      <Checkbox.Root
                        className={`w-[14px] h-[14px] border border-primary-0 rounded flex items-center justify-center ${
                          selectedLabels?.includes(item.id)
                            ? "border"
                            : "border-transparent group-hover:border-primary-0"
                        }`}
                        checked={selectedLabels?.includes(item.id)}
                      >
                        <Checkbox.Indicator className="w-full h-full flex items-center justify-center bg-primary-0 text-white">
                          <CheckIcon />
                        </Checkbox.Indicator>
                      </Checkbox.Root>
                      {item.color && (
                        <Checkbox.Root
                          className="w-[10px] h-[10px] border rounded-[3px] flex items-center justify-center"
                          style={{ borderColor: item.color.value }}
                          defaultChecked
                        >
                          <Checkbox.Indicator
                            className="w-full h-full flex items-center justify-center"
                            style={{ backgroundColor: item.color.value }}
                          ></Checkbox.Indicator>
                        </Checkbox.Root>
                      )}
                      <span>{item.label}</span>
                    </DropdownMenuItem>
                  ))}
              </>
            )}
            {(!labels || labels?.length < 1) && (
              <div className="flex gap-2 items-center text-xs !p-1">
                <div className="border border-secondary-n-900 bg-transparent hover:bg-transparent h-6 w-6 text-xs rounded-md text-secondary-n-400 flex items-center justify-center">
                  <BookmarkX className="size-4" />
                </div>
                No labels created
              </div>
            )}
          </>
        )}

        {page === "create" && (
          <CreateNewLabel
            newLabelVal={newLabelVal}
            setNewLabelVal={setNewLabelVal}
            setPage={setPage}
            // @ts-expect-error TS2322
            setOpen={setOpen}
          />
        )}

        {page === "color" && (
          <ColorSelectList
            setPage={setPage}
            // @ts-expect-error TS2322
            setOpen={setOpen}
            handleColorSelect={handleColorSelect}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const CreateNewLabel = ({
  newLabelVal,
  setNewLabelVal,
  setPage,
  setOpen,
}: {
  newLabelVal: string;
  setNewLabelVal: (newLabelVal: string) => void;
  setPage: (page: string) => void;
  setOpen: (open: boolean) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <>
      <Input
        ref={inputRef}
        placeholder="New label"
        className="!border-none focus:!ring-0 focus-visible:!right-0 px-2 h-[28px] text-xs"
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") {
            e.preventDefault();
            setPage("color");
          }
        }}
        onKeyUp={(e) => e.stopPropagation()}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setNewLabelVal(e.target.value)
        }
        onClick={(event) => event.stopPropagation()}
      />
      <DropdownMenuItem
        disabled={!newLabelVal}
        onSelect={(event) => event.preventDefault()}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
          setPage("color");
        }}
        className="flex gap-1 text-xs cursor-pointer bg-secondary-n-900 text-text-primary text-ellipsis overflow-hidden"
      >
        <PlusIcon className="min-w-4 h-4" />
        <span className="text-nowrap text-xs text-ellipsis overflow-hidden w-full">
          New Label: {newLabelVal}
        </span>
      </DropdownMenuItem>
    </>
  );
};

const ColorSelectList = ({
  setPage,
  setOpen,
  handleColorSelect,
}: {
  setPage: (page: string) => void;
  setOpen: (open: boolean) => void;
  handleColorSelect: (color: ColorItem) => void;
}) => {
  return (
    <>
      <DropdownMenuItem
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          setPage("create");
          setOpen(true);
        }}
        className="flex justify-between text-xs mr-1"
      >
        Pick a color for label
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {COLOR_LIST.map((colorItem) => (
        <DropdownMenuItem
          key={colorItem.key}
          onSelect={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            handleColorSelect(colorItem);
          }}
          className="group text-xs cursor-pointer gap-1"
        >
          <div className="flex gap-2 items-center">
            <Checkbox.Root
              className="w-2.5 h-2.5 border rounded flex items-center justify-center"
              style={{ borderColor: colorItem.value }}
              defaultChecked
            >
              <Checkbox.Indicator
                className="w-full h-full flex items-center justify-center"
                style={{ backgroundColor: colorItem.value }}
              ></Checkbox.Indicator>
            </Checkbox.Root>
            {colorItem.label}
          </div>
        </DropdownMenuItem>
      ))}
    </>
  );
};
